import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Heart, Mail, Lock, User, AlertCircle, ArrowLeft } from "lucide-react";
import { apiClient } from "@/api/ApiClient";
import { useAuth } from "@/contexts/AuthContext";
import type { AuthSessionResponse } from "@/auth/authTypes";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { SocialLoginButtons, hasSocialLogin } from "@/components/SocialLoginButtons";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onComplete: (userData: { id: string; email: string; name?: string }, isSignup?: boolean) => void;
  character?: {
    name: string;
    avatar: string;
    personality: string;
    communicationStyle: string;
    style: string;
  };
}

export const AuthModal = ({ isOpen, onClose, onComplete, character }: AuthModalProps) => {
  const { applySession } = useAuth();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isForgotPassword, setIsForgotPassword] = useState(false);
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [passwordResetCooldownUntil, setPasswordResetCooldownUntil] = useState<number | null>(null);
  const [nowTs, setNowTs] = useState(() => Date.now());
  const { toast } = useToast();
  const { t } = useTranslation();

  const applyPasswordResetCooldown = useCallback((nextResendAvailableAt: string | null | undefined) => {
    if (!nextResendAvailableAt) return;
    const t = new Date(nextResendAvailableAt).getTime();
    if (!Number.isNaN(t) && t > Date.now()) {
      setPasswordResetCooldownUntil(t);
    }
  }, []);

  useEffect(() => {
    if (!isForgotPassword || !email.trim()) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiClient.get(
          `/auth/password-reset-resend-available?email=${encodeURIComponent(email.trim())}`,
        );
        if (!cancelled) applyPasswordResetCooldown(data?.nextResendAvailableAt);
      } catch {
        /* ignore — user may be offline */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isForgotPassword, email, applyPasswordResetCooldown]);

  useEffect(() => {
    if (!isForgotPassword || !passwordResetCooldownUntil || Date.now() >= passwordResetCooldownUntil) {
      return;
    }
    const id = window.setInterval(() => setNowTs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isForgotPassword, passwordResetCooldownUntil]);

  useEffect(() => {
    if (passwordResetCooldownUntil != null && nowTs >= passwordResetCooldownUntil) {
      setPasswordResetCooldownUntil(null);
    }
  }, [nowTs, passwordResetCooldownUntil]);

  const passwordResetCooldownRemainingMs =
    passwordResetCooldownUntil != null && nowTs < passwordResetCooldownUntil
      ? passwordResetCooldownUntil - nowTs
      : 0;

  const formatCooldown = (ms: number) => {
    const s = Math.max(0, Math.ceil(ms / 1000));
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  const handleForgotPassword = async () => {
    if (!email) {
      setError('Please enter your email address first.');
      return;
    }

    setError('');
    setForgotPasswordLoading(true);
    try {
      const data = await apiClient.post('/auth/password/reset', { email: email.trim() });
      applyPasswordResetCooldown(data?.nextResendAvailableAt);

      if (data?.success) {
        toast({
          title: "Password reset",
          description:
            data.message ??
            "If an account exists for this email, password reset instructions will be sent.",
        });
        setIsForgotPassword(false);
      } else {
        toast({
          variant: "destructive",
          title: "Please wait",
          description: data?.message ?? "You can request another reset link after the cooldown.",
        });
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "An unexpected error occurred. Please try again.";
      setError(msg);
      toast({
        variant: "destructive",
        title: "Request failed",
        description: msg,
      });
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const validatePassword = (password: string) => {
    const errors = [];
    if (password.length < 8) {
      errors.push("at least 8 characters");
    }
    if (!/[A-Z]/.test(password)) {
      errors.push("one uppercase letter");
    }
    if (!/[0-9]/.test(password)) {
      errors.push("one number");
    }
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      errors.push("one special character");
    }
    return errors;
  };

  const passwordErrors = !isLogin ? validatePassword(password) : [];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isLogin) {
        const response = (await apiClient.post('/auth/login', { email, password })) as AuthSessionResponse;
        const u = response?.user;
        if (!u?.id || !u?.email) {
          setError('Invalid login response');
          return;
        }
        await applySession(response);
        onComplete({ id: String(u.id), email: u.email!, name: u.name ?? undefined }, false);
        toast({
          title: "Welcome back! 💕",
          description: "You've successfully logged in.",
        });
      } else {
        const response = (await apiClient.post('/auth/signup', { email, password, name })) as AuthSessionResponse;
        const u = response?.user;
        if (!u?.id || !u?.email) {
          setError('Invalid signup response');
          return;
        }
        await applySession(response);
        onComplete({ id: String(u.id), email: u.email!, name: u.name ?? undefined }, true);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto bg-gradient-dreamy border-primary/20" onOpenAutoFocus={(e) => e.preventDefault()} hideCloseButton>
        <button
          type="button"
          onClick={onClose}
          className="absolute left-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        >
          <ArrowLeft className="h-4 w-4" />
          <span className="sr-only">Back</span>
        </button>
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-bold bg-gradient-romantic bg-clip-text text-transparent">
            {isForgotPassword ? t('auth.reset_password') : isLogin ? t('auth.welcome_back') : t('auth.join')}
          </DialogTitle>
        </DialogHeader>
        
        {isForgotPassword && (
          <div className="text-center mb-6">
            <div className="p-4">
              <p className="text-sm text-muted-foreground">
                {t('auth.reset_password_description')}
              </p>
            </div>
          </div>
        )}

        {error && (
          <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <p className="text-sm text-destructive">{error}</p>
          </div>
        )}

        {!isForgotPassword && hasSocialLogin() && (
          <div className="space-y-4 mb-4">
            <SocialLoginButtons
              disabled={loading}
              onSession={async (session) => {
                await applySession(session);
                const u = session.user;
                onComplete({ id: String(u.id), email: u.email ?? '', name: u.name ?? undefined }, false);
              }}
            />
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <div className="h-px flex-1 bg-primary/20" />
              {t('auth.or')}
              <div className="h-px flex-1 bg-primary/20" />
            </div>
          </div>
        )}

        {isForgotPassword ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                {t('auth.email')}
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder={t('auth.enter_email')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 border-primary/20 focus:border-primary"
                  required
                />
              </div>
            </div>

            <Button
              onClick={handleForgotPassword}
              variant="romantic"
              className="w-full"
              disabled={
                forgotPasswordLoading ||
                (!!passwordResetCooldownUntil && nowTs < passwordResetCooldownUntil)
              }
            >
              <Mail className="h-4 w-4 mr-2" />
              {forgotPasswordLoading
                ? t('auth.sending')
                : passwordResetCooldownRemainingMs > 0
                  ? `${t('auth.wait')} ${formatCooldown(passwordResetCooldownRemainingMs)}`
                  : t('auth.send_reset_email')}
            </Button>
            {passwordResetCooldownRemainingMs > 0 && (
              <p className="text-center text-xs text-muted-foreground">
                {t('auth.cooldown_message', { time: formatCooldown(passwordResetCooldownRemainingMs) })}
              </p>
            )}
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
          {!isLogin && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                {t('auth.your_name')}
              </Label>
              <div className="relative">
                <User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  id="name"
                  type="text"
                  placeholder={t('auth.enter_name')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 border-primary/20 focus:border-primary"
                  required
                />
              </div>
            </div>
          )}

          
          <div className="space-y-2">
            <Label htmlFor="email" className="text-sm font-medium">
              {t('auth.email')}
            </Label>
            <div className="relative">
              <Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                placeholder={t('auth.enter_email')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 border-primary/20 focus:border-primary"
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-sm font-medium">
              {t('auth.password')}
            </Label>
            <div className="relative">
              <Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="password"
                type="password"
                placeholder={t('auth.enter_password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 border-primary/20 focus:border-primary"
                required
              />
            </div>
            {!isLogin && password && passwordErrors.length > 0 && (
              <div className="text-xs text-muted-foreground mt-1">
                <p className="mb-1">{t('auth.password_must_include')}</p>
                <ul className="space-y-1">
                  {passwordErrors.map((error, index) => (
                    <li key={index} className="flex items-center gap-1">
                      <span className="text-destructive">✗</span>
                      {error}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!isLogin && password && passwordErrors.length === 0 && (
              <div className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <span>✓</span>
                {t('auth.password_ok')}
              </div>
            )}
            {isLogin && (
              <button
                type="button"
                onClick={() => setIsForgotPassword(true)}
                className="text-xs text-muted-foreground hover:text-primary transition-colors"
              >
                {t('auth.forgot_password')}
              </button>
            )}
          </div>

            <Button
              type="submit"
              variant="romantic"
              className="w-full"
              disabled={loading || (!isLogin && passwordErrors.length > 0)}
            >
              <Heart className="h-4 w-4 mr-2" />
              {loading ? t('auth.loading') : isLogin ?
                (character ? t('auth.chat_with', { name: character.name }) : t('auth.sign_in')) :
                t('auth.create_account')
              }
            </Button>
          </form>
        )}
        
        {isForgotPassword && (
          <div className="text-center">
            <button
              type="button"
              onClick={() => {
                setIsForgotPassword(false);
                setPasswordResetCooldownUntil(null);
                setError('');
              }}
              className="text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              {t('auth.back_to_sign_in')}
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
